import { DatePicker, TextField, Text, Toggle, fromUtcDateValue, toUtcDateValue } from '@cinesend/ui-kit'
import { FilmStrip, SpeakerHigh } from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import useStore from '../../hooks/use_store'
import Dropdown from './dropdown'
import ImageField from '../image_field'
import FileSelector from './file_selector'
import { useNavigate, useParams } from 'react-router-dom'
import isEmpty from '../../helpers/is_empty'
import { useQueryClient } from '@tanstack/react-query'

const FieldValue = ({
  className = '',
  field,
  assetID,
  cycleID,
  languageID = null,
  isLoading,
  disabled,
  refreshValues,
  cacheKey,
}) => {
  const { updateModel, createModel } = useStore()
  const fieldValue = field.value
  const fieldType = field.field_type
  const [value, setValue] = useState(fieldValue?.value ?? fieldValue?.file_name ?? '')
  const params = useParams()
  const navigate = useNavigate()
  const isDisabled = disabled || field?.is_external_api_value_source
  const queryClient = useQueryClient()

  const issues = (fieldValue?.issues || []).concat(field?.asset_image?.issues || [])
  const hasIssues = issues.length > 0 || (field.is_required && isEmpty(value))
  const fieldError =
    issues.length > 0 ? issues.map((issue) => issue.description).join('\n') : hasIssues ? 'Required' : undefined
  const fieldName = field.name + (field.is_required ? ' *' : '')

  useEffect(() => {
    setValue(fieldValue?.value ?? fieldValue?.file_name ?? '')
  }, [fieldValue, field])

  const refreshAsset = () => queryClient.invalidateQueries([`cycles/${cycleID}/assets/${assetID}`])
  const saveField = (newValue) => {
    // save only if value has changed
    if ((isEmpty(fieldValue?.value) && isEmpty(newValue)) || fieldValue?.value === newValue) {
      return
    }

    setValue(newValue)

    // If the field value already exists, we're just updating the value. Otherwise, we create it.
    if (!isEmpty(fieldValue?.value)) {
      updateModel.mutate({
        endpoint: 'field-values',
        id: fieldValue.id,
        data: {
          value: newValue,
          cycle_id: cycleID,
          field_id: field.id,
        },
        onSuccess: (res) => {
          setValue(res?.data?.field?.value)
          // update asset id if changed
          if (res.data.field.asset_id !== assetID) {
            navigate(
              `/airlines/${params.airlineID}/cycles/${cycleID}/metadata/${params.assetType}/${res.data.field.asset_id}/details`
            )
          } else {
            refreshAsset()
            refreshValues()
          }
        },
        onError: (error) => {
          refreshAsset()
          refreshValues()
        },
      })
    } else {
      let data = {
        field_id: field.id,
        asset_id: parseInt(assetID),
        value: newValue,
        cycle_id: cycleID,
      }

      // Do not send language_id if this is a dropdown
      if (fieldType !== 'dropdown') {
        data['language_id'] = languageID
      }

      createModel.mutate(
        {
          endpoint: 'field-values',
          data,
        },
        {
          onSuccess: (res) => {
            if (res.data.field.asset_id !== assetID) {
              refreshValues()
            }
            setValue(res?.data?.field?.value)
            refreshAsset()
            refreshValues()
          },
          onError: (error) => {
            refreshAsset()
            refreshValues()
          },
        }
      )
    }
  }

  return (
    <div className={`${className} w-full`}>
      {fieldType === 'string' && (
        <div className='flex items-end space-x-2'>
          <TextField
            leadingContent={fieldName?.toLocaleLowerCase() === 'copyright' ? '©' : null}
            label={fieldName}
            value={value}
            disabled={isDisabled}
            onChange={(e) => setValue(e.target.value)}
            onBlur={(e) => saveField(e.target.value)}
            error={fieldError}
          />
        </div>
      )}
      {['video', 'audio'].includes(fieldType) && (
        <div className='flex w-full items-start gap-4'>
          <div className='flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[color:var(--cs-color-border-default)] bg-[var(--cs-color-background-secondary)]'>
            <div className='flex flex-col items-center gap-1 px-2 text-center'>
              {fieldType === 'audio' ? (
                <SpeakerHigh
                  size={28}
                  weight='regular'
                  aria-hidden='true'
                  className='text-[color:var(--cs-color-foreground-secondary)]'
                />
              ) : (
                <FilmStrip
                  size={28}
                  weight='regular'
                  aria-hidden='true'
                  className='text-[color:var(--cs-color-foreground-secondary)]'
                />
              )}
              <Text variant='xs' tone='secondary'>
                No preview
              </Text>
            </div>
          </div>
          <div className='flex min-w-0 flex-1 items-start space-x-2'>
            <TextField
              label={fieldName}
              value={value}
              disabled={true} // Disable input for video/audio fields. Let the file selector handle it.
              onChange={(e) => setValue(e.target.value)}
              onBlur={(e) => saveField(e.target.value)}
              error={fieldError}
            />
            <FileSelector
              cycleID={cycleID}
              assetID={assetID}
              value={value}
              field={field}
              isLoading={isLoading}
              saveField={refreshValues}
              refreshAsset={refreshAsset}
            />
          </div>
        </div>
      )}
      {fieldType === 'datetime' && (
        <DatePicker
          fullWidth
          label={fieldName}
          value={toUtcDateValue(value)}
          disabled={isDisabled}
          onValueChange={(value) => saveField(fromUtcDateValue(value))}
          error={fieldError}
        />
      )}
      {fieldType === 'dropdown' && (
        <Dropdown
          cycleID={cycleID}
          field={field}
          label={fieldName}
          value={value}
          disabled={isDisabled}
          isLoading={isLoading}
          languageID={languageID}
          saveField={saveField}
          error={fieldError}
        />
      )}
      {fieldType === 'image' && (
        <ImageField
          assetID={assetID}
          field={field}
          value={value}
          message={fieldError}
          saveField={saveField}
          refreshAsset={refreshAsset}
          disabled={isDisabled}
          cacheKey={cacheKey}
        />
      )}
      {fieldType === 'boolean' && (
        <div className='space-y-1'>
          <Toggle
            text={fieldName}
            pressed={value === 'true'}
            onToggle={() => saveField(value === 'true' ? 'false' : 'true')}
          />
          {fieldError && (
            <Text variant='xs' tone='error'>
              {fieldError}
            </Text>
          )}
        </div>
      )}
      {fieldType === 'number' && (
        <div className='flex items-end space-x-2'>
          <TextField
            label={fieldName}
            type={'number'}
            value={value}
            disabled={isDisabled}
            onChange={(e) => setValue(e.target.value)}
            onBlur={(e) => saveField(e.target.value)}
            error={fieldError}
            id={`field-value-${field.value?.id}`}
          />
        </div>
      )}
    </div>
  )
}

export default FieldValue
